import { useMemo, useState } from 'react';
import { EVENT_CATEGORIES, CATEGORY_GROUPS, MAX_EVENT_CATEGORIES, QUICK_CATEGORIES, categoryLabel } from '../constants/eventCategories';
import { SearchPill, TextInput, Help } from './host/kit';

/**
 * CategoryMultiSelect — searchable, grouped, multi-select category picker (Phase 9),
 * drawn as the CATEGORIES row of the host event edit handoff: a grey panel holding the
 * selected chips, a white search pill, the grouped option pills and the `n/3 SELECTED`
 * footer; the "Name your category" input sits below the panel when `other` is picked.
 * The first selected category is the PRIMARY (drives Featured "1 per category").
 * Capped at MAX_EVENT_CATEGORIES. Renders inside the wizard's `.hev` scope.
 *
 * Props:
 *   value: string[]        selected slugs (order matters — [0] is primary)
 *   onChange(slugs)        called with the new selection
 *   customValue: string    free text when "other" is selected
 *   onCustomChange(text)
 *   error: string          when set the panel takes the red border (the page prints the message)
 *   variant: 'full' | 'chips'  `chips` is the Simple view's compact row: the eleven quick picks,
 *                          one tap sets the PRIMARY (categories[0]); anything picked in Advanced
 *                          stays behind it (cap 3); tapping the active chip clears it.
 */
export default function CategoryMultiSelect({ value = [], onChange, customValue = '', onCustomChange, error, variant = 'full' }) {
  const [query, setQuery] = useState('');
  const selected = value || [];

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? EVENT_CATEGORIES.filter((c) => c.label.toLowerCase().includes(q)) : EVENT_CATEGORIES),
    [q],
  );
  const groups = useMemo(
    () => CATEGORY_GROUPS
      .map((g) => ({ group: g, items: filtered.filter((c) => c.group === g) }))
      .filter((g) => g.items.length > 0),
    [filtered],
  );

  if (variant === 'chips') {
    const primary = selected[0] || null;
    const pick = (slug) => {
      if (primary === slug) { onChange(selected.slice(1)); return; }
      onChange([slug, ...selected.filter((s) => s !== slug)].slice(0, MAX_EVENT_CATEGORIES));
    };
    return (
      <div className="hev-qchips" role="radiogroup" aria-label="Category" data-error={error ? '' : undefined} data-testid="category-chips">
        {QUICK_CATEGORIES.map((c) => {
          const on = primary === c.slug;
          return (
            <button key={c.slug} type="button" role="radio" aria-checked={on} className="hev-qchip" data-on={on ? '' : undefined} onClick={() => pick(c.slug)}>
              {c.label}
            </button>
          );
        })}
      </div>
    );
  }

  const atMax = selected.length >= MAX_EVENT_CATEGORIES;
  const hasOther = selected.includes('other');

  const toggle = (slug) => {
    if (selected.includes(slug)) {
      onChange(selected.filter((s) => s !== slug));
    } else if (!atMax) {
      onChange([...selected, slug]);
    }
  };

  const custom = (customValue || '').trim();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div className="hev-cats" data-error={error ? '' : undefined}>
        {/* Selected chips — the first one is the primary (★). */}
        <div className="hev-chips">
          {selected.length === 0 ? (
            <span style={{ fontSize: 11, color: '#9CA3AF', paddingLeft: 6 }}>Pick at least one below.</span>
          ) : (
            selected.map((slug, i) => (
              <span key={slug} className="hev-chip" title={i === 0 ? 'Primary category' : undefined}>
                {i === 0 ? '★ ' : ''}
                {slug === 'other' && custom ? custom : categoryLabel(slug)}
                <button type="button" aria-label={`Remove ${categoryLabel(slug)}`} onClick={() => toggle(slug)}>×</button>
              </span>
            ))
          )}
        </div>

        {/* Search */}
        <SearchPill
          white
          small
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search categories…"
          aria-label="Search categories"
        />

        {/* Grouped option pills */}
        <div className="hev-catlist">
          {groups.length === 0 ? (
            <span className="hev-cats-foot" style={{ color: '#9CA3AF' }}>No categories match “{query}”</span>
          ) : (
            groups.map(({ group, items }) => (
              <div key={group} className="hev-catgroup">
                <span className="hev-catgroup-n">{group}</span>
                <div className="hev-catopts">
                  {items.map((c) => {
                    const isSel = selected.includes(c.slug);
                    const disabled = !isSel && atMax;
                    return (
                      <button
                        key={c.slug}
                        type="button"
                        className="hev-catopt"
                        data-on={isSel ? '' : undefined}
                        aria-pressed={isSel}
                        disabled={disabled}
                        onClick={() => toggle(c.slug)}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <span className="hev-cats-foot">{selected.length}/{MAX_EVENT_CATEGORIES} selected · first is primary</span>
      </div>

      {/* Custom text for "Other" — becomes the category label (shown on the chip above). */}
      {hasOther && (
        <>
          <TextInput
            type="text"
            value={customValue}
            onChange={(e) => onCustomChange?.(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
            maxLength={60}
            placeholder="Name your category"
            aria-label="Name your category"
          />
          <Help>
            {custom
              ? `Saved as “${custom}” — press Enter to confirm.`
              : 'Type the category you had in mind. It becomes the label on the chip.'}
          </Help>
        </>
      )}
    </div>
  );
}
